import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { patchPersonalDataFormGroup } from '@app/helpers/personal-data-form-patch.helper';
import { ProfileEditComponent } from '@app/components/profile-edit/profile-edit.component';
import { PersonalDataFormGroup } from '@app/models/form.models';
import { getPersonalDataFormGroup } from '@app/pages/registration-wizard/registration-wizard-form.helper';
import { ToastService } from '@app/services/toast.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { CardComponent } from '@app/ui-components/card/card.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-person-data',
  imports: [
    ReactiveFormsModule,
    ProfileEditComponent,
    CardComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './person-data.component.html',
  styleUrl: './person-data.component.scss',
})
export class PersonDataComponent {
  personDataFormGroup: FormGroup<PersonalDataFormGroup>;

  save() {
    if (this.personDataFormGroup.invalid) {
      this.personDataFormGroup.markAllAsTouched();
      this.toast.error(
        this.translate.instant('profile.toasts.fixValidation'),
      );
      return;
    }

    const rawValue = this.personDataFormGroup.getRawValue();

    this.store
      .savePersonalData({
        titleCode: rawValue.title,
        firstName: rawValue.givenName,
        midName: rawValue.middleName,
        lastName: rawValue.familyName,
        citizenship: rawValue.citizenship,
        birthPlace: rawValue.birthPlace,
        birthDate: rawValue.dateOfBirth,
        email: '',
        motherFirstName: rawValue.motherFirstName,
        motherMidName: rawValue.motherMiddleName,
        motherLastName: rawValue.motherLastName,
        maidenFirstName: rawValue.birthFirstName,
        maidenMidName: rawValue.birthMiddleName,
        maidenLastName: rawValue.birthLastName,
        idCardType: rawValue.certificateType ?? 0,
        idCardNumber: rawValue.numberOfCertificate,
        idCardNumberExpiry: rawValue.expiryDate,
        taxIdentifier: rawValue.optionalTaxNumber,
        bankAccountNumber: rawValue.optionalBankNumber,
        address: {
          countryCode: rawValue.country || null,
          zipCode: rawValue.postalCode || null,
          city: rawValue.city || null,
          publicSpace: rawValue.publicSpace || null,
          publicSpaceType: rawValue.publicSpaceType || null,
          houseNumber: rawValue.houseNumber || null,
          doorOrFloor: rawValue.doorOrFloor || null,
        },
      })
      .pipe(
        tap(() =>
          this.toast.success(
            this.translate.instant('profile.toasts.savedSuccessfully'),
          ),
        ),
        catchError(() => {
          this.toast.error(
            this.translate.instant('profile.toasts.saveFailed'),
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  cancelEdit() {
    this.patchFormWithStoreData();
  }

  protected store = inject(AuthStore);
  protected genericStore = inject(GenericStore);

  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  constructor(fb: FormBuilder) {
    this.personDataFormGroup = getPersonalDataFormGroup(fb);

    effect(() => {
      const personalData = this.store.user()?.personalData;
      const nameTitles = this.genericStore.nameTitlesEntities();
      if (!personalData || nameTitles.length === 0) {
        return;
      }
      patchPersonalDataFormGroup(
        this.personDataFormGroup,
        personalData,
        nameTitles,
      );
    });
  }

  private patchFormWithStoreData(): void {
    const personalData = this.store.user()?.personalData;
    const nameTitles = this.genericStore.nameTitlesEntities();
    if (!personalData || nameTitles.length === 0) {
      return;
    }
    patchPersonalDataFormGroup(
      this.personDataFormGroup,
      personalData,
      nameTitles,
    );
  }
}
